import type { RouteRecordRaw } from 'vue-router'
import CategoriesPage from '@/pages/CategoriesPage.vue'
import LandingPage from '@/pages/LandingPage.vue'
import AuthPage from '@/pages/AuthPage.vue'
import AdminPage from '@/pages/AdminPage.vue'
import AdminBotPage from '@/pages/AdminBotPage.vue'
import AdminBotProfilesPage from '@/pages/AdminBotProfilesPage.vue'
import OpenLinkPage from '@/pages/OpenLinkPage.vue'
import SettingsPage from '@/pages/SettingsPage.vue'
import TodoCalendarsPage from '@/pages/TodoCalendarsPage.vue'
import TodoDetailPage from '@/pages/TodoDetailPage.vue'
import TodoListPage from '@/pages/TodoListPage.vue'
import { MOVED_SECTIONS } from '@/settings/subsections'
import { useTodosStore } from '@/stores/todos'
import { todosEntryRedirect } from '@/todos/entry'
import { takeStartCalendar } from '@/startParam'
import { getStartParam } from '@/telegramWebApp'

export const routes: RouteRecordRaw[] = [
  // (never rendered: the guard sends a signed-in visitor to /todos and everybody else to the landing page)
  { path: '/', name: 'home', component: LandingPage },
  { path: '/welcome', name: 'landing', component: LandingPage, meta: { public: true } },
  {
    path: '/todos',
    name: 'todos',
    component: TodoCalendarsPage,
    // With one list only, the Todos tab (and the website, the Mini App) goes straight to it; `?browse=1` is the back arrow of
    // a list, which does want the lists page. The calendars are read fresh, a failure just shows the page (and its error).
    beforeEnter: async (to) => {
      if (to.query.browse) return true
      // The "Open in App" button under a posted list: `?startapp=c<calendar id>` opens that list.
      const startCalendar = takeStartCalendar(getStartParam())
      if (startCalendar !== null) return { name: 'todo-list', params: { calendarId: startCalendar } }
      const store = useTodosStore()
      try {
        await store.loadCalendars()
      } catch {
        return true
      }
      return todosEntryRedirect(store.calendars) ?? true
    },
  },
  { path: '/todos/:calendarId(\\d+)', name: 'todo-list', component: TodoListPage },
  // A landing page for one tag of the calendar, like a blog's category links.
  { path: '/todos/:calendarId(\\d+)/tag/:tag', name: 'todo-tag', component: TodoListPage },
  { path: '/todos/:calendarId(\\d+)/:uid', name: 'todo-detail', component: TodoDetailPage },
  // Every tag in use on any list, each opening its tag page.
  { path: '/categories', name: 'categories', component: CategoriesPage },
  { path: '/accounts', redirect: '/settings/accounts' },
  { path: '/posts', redirect: '/settings/debug/posts' },
  {
    path: '/settings',
    name: 'settings',
    component: SettingsPage,
    // (the old link to the accounts card, from before the settings had a page per category)
    beforeEnter: (to) => (to.hash === '#accounts' ? '/settings/accounts' : true),
  },
  // (the categories that moved below another one, e.g. `/settings/protocols`)
  { path: '/settings/protocols', redirect: MOVED_SECTIONS.protocols },
  { path: '/settings/posts', redirect: MOVED_SECTIONS.posts },
  // (`rest` is what a category has below it, e.g. `/settings/tags/5/aliases`)
  { path: '/settings/:section/:rest*', name: 'settings-section', component: SettingsPage },
  // The handler of the `web+todo:` and `geo:` links the browser passes on (`protocols/register.ts`), `?uri=` is the link.
  { path: '/open', name: 'open-link', component: OpenLinkPage },
  { path: '/auth', name: 'auth', component: AuthPage },
  { path: '/admin', name: 'admin', component: AdminPage, meta: { requiresAdmin: true } },
  { path: '/admin/bot', name: 'admin-bot', component: AdminBotPage, meta: { requiresAdmin: true } },
  // The names, descriptions and commands of the bot: `profiles` for everybody, `profiles/chats` for single admin chats.
  { path: '/admin/bot/profiles', name: 'admin-bot-profiles', component: AdminBotProfilesPage, meta: { requiresAdmin: true } },
  { path: '/admin/bot/profiles/chats', name: 'admin-bot-chats', component: AdminBotProfilesPage, meta: { requiresAdmin: true } },
  // (after the bot routes, which are not sections)
  { path: '/admin/:section', name: 'admin-section', component: AdminPage, meta: { requiresAdmin: true } },
]
